import axios from "axios"
import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import {
    EMAIL,
    FALLBACK_PRODUCTS,
    ORGANIZATION_ID,
    ORGANIZATION_NAME,
    PHONE,
    SITE_NAME,
    WEBSITE_ID,
    getBaseUrl,
    getPageMeta,
} from "./seoConfig"

const SCRIPT_ID = "neurosfera-json-ld"

function removeEmpty(value) {
    if (Array.isArray(value)) {
        return value.map(removeEmpty).filter((item) => item !== undefined)
    }

    if (!value || typeof value !== "object") {
        return value === "" || value === null || value === undefined ? undefined : value
    }

    return Object.entries(value).reduce((result, [key, entry]) => {
        const cleaned = removeEmpty(entry)

        if (cleaned !== undefined && (!Array.isArray(cleaned) || cleaned.length > 0)) {
            result[key] = cleaned
        }

        return result
    }, {})
}

function toAbsoluteUrl(value, baseUrl) {
    if (!value) return undefined

    try {
        return new URL(value, baseUrl).toString()
    } catch {
        return undefined
    }
}

function toSchemaImageUrl(value, baseUrl) {
    if (!value || value.startsWith("data:") || value.startsWith("blob:")) return undefined

    return toAbsoluteUrl(value, baseUrl)
}

function mergeProducts(primaryProducts, fallbackProducts = FALLBACK_PRODUCTS) {
    const productMap = new Map()

    fallbackProducts.forEach((product) => {
        productMap.set(String(product.key), product)
    })

    primaryProducts.forEach((product) => {
        productMap.set(String(product.key), product)
    })

    return Array.from(productMap.values()).filter(isPublicProduct)
}

function isPublicProduct(product) {
    return !product?.hidden
}

function hasProductPage(product) {
    return Boolean(
        product?.page_title ||
        product?.page_summary ||
        product?.page_content ||
        product?.page_images ||
        product?.features?.length ||
        product?.page_use_cases ||
        product?.page_cta
    )
}

function productToJsonLd(product, baseUrl) {
    const productUrl = hasProductPage(product)
        ? toAbsoluteUrl(`/product/${product.key}`, baseUrl)
        : toAbsoluteUrl("/product", baseUrl)

    return removeEmpty({
        "@type": "Product",
        "@id": `${productUrl}#product`,
        name: product.page_title || product.module,
        description: product.page_summary || product.design,
        image: toSchemaImageUrl(product.image, baseUrl),
        category: "Software",
        brand: {
            "@id": `${baseUrl}${ORGANIZATION_ID}`,
        },
        url: productUrl,
        offers: product.info_only ? undefined : {
            "@type": "Offer",
            price: product.price,
            priceCurrency: "RUB",
            availability: "https://schema.org/InStock",
            url: productUrl,
            seller: {
                "@id": `${baseUrl}${ORGANIZATION_ID}`,
            },
        },
    })
}

function getBreadcrumbs(pathname, pageName, baseUrl) {
    const items = [
        {
            "@type": "ListItem",
            position: 1,
            name: SITE_NAME,
            item: toAbsoluteUrl("/", baseUrl),
        },
    ]

    if (pathname !== "/") {
        items.push({
            "@type": "ListItem",
            position: 2,
            name: pageName,
            item: toAbsoluteUrl(pathname, baseUrl),
        })
    }

    return {
        "@type": "BreadcrumbList",
        itemListElement: items,
    }
}

export default function StructuredData() {
    const location = useLocation()
    const [products, setProducts] = useState([])

    useEffect(() => {
        if (!location.pathname.startsWith("/product")) return undefined

        let isActive = true

        async function fetchProducts() {
            try {
                const response = await axios.get("/api/products")

                if (isActive) {
                    setProducts((response.data.data ?? []).filter(isPublicProduct))
                }
            } catch (error) {
                if (isActive) {
                    setProducts([])
                }
            }
        }

        fetchProducts()

        return () => {
            isActive = false
        }
    }, [location.pathname])

    const jsonLd = useMemo(() => {
        const baseUrl = getBaseUrl()
        const pageUrl = toAbsoluteUrl(location.pathname, baseUrl)
        const availableProducts = mergeProducts(products)
        const productId = location.pathname.match(/^\/product\/([^/]+)$/)?.[1]
        const currentProduct = productId
            ? availableProducts.find((item) => String(item.key) === decodeURIComponent(productId))
            : null
        const pageMeta = getPageMeta(location.pathname, currentProduct)
        const graph = [
            {
                "@type": "Organization",
                "@id": `${baseUrl}${ORGANIZATION_ID}`,
                name: ORGANIZATION_NAME,
                url: baseUrl,
                logo: toAbsoluteUrl("/neurosferaLogo.png", baseUrl),
                email: EMAIL,
                telephone: PHONE,
            },
            {
                "@type": "WebSite",
                "@id": `${baseUrl}${WEBSITE_ID}`,
                name: SITE_NAME,
                url: baseUrl,
                publisher: {
                    "@id": `${baseUrl}${ORGANIZATION_ID}`,
                },
            },
            {
                "@type": "WebPage",
                "@id": `${pageUrl}#webpage`,
                name: pageMeta.name,
                description: pageMeta.description,
                url: pageUrl,
                isPartOf: {
                    "@id": `${baseUrl}${WEBSITE_ID}`,
                },
                about: {
                    "@id": `${baseUrl}${ORGANIZATION_ID}`,
                },
                breadcrumb: getBreadcrumbs(location.pathname, pageMeta.name, baseUrl),
            },
        ]

        if (location.pathname === "/product" && availableProducts.length > 0) {
            graph.push({
                "@type": "ItemList",
                "@id": `${pageUrl}#product-list`,
                name: "Продукты Нейросфера",
                itemListElement: availableProducts.map((product, index) => ({
                    "@type": "ListItem",
                    position: index + 1,
                    item: productToJsonLd(product, baseUrl),
                })),
            })
        }

        if (currentProduct) {
            graph.push(productToJsonLd(currentProduct, baseUrl))
        }

        return removeEmpty({
            "@context": "https://schema.org",
            "@graph": graph,
        })
    }, [location.pathname, products])

    useEffect(() => {
        const previousScript = document.getElementById(SCRIPT_ID)

        if (previousScript) {
            previousScript.remove()
        }

        const script = document.createElement("script")
        script.id = SCRIPT_ID
        script.type = "application/ld+json"
        script.text = JSON.stringify(jsonLd)

        document.head.appendChild(script)

        return () => {
            script.remove()
        }
    }, [jsonLd])

    return null
}
